'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { animated, easings, useSpring } from '@react-spring/web';

// ----------------------------------------------------------------------

export type Flygning = {
  /** En bild av det som flyger, som data-URL. Utan bild flyger en enkel ruta med etiketten. */
  bild: string | null;
  etikett: string;
  /** Där flygningen börjar: skärmposition och storlek. */
  från: { x: number; y: number; bredd: number; höjd: number };
  /** Väljaren för det element flygningen ska landa i. Letas upp först när sidan hunnit montera. */
  målVäljare: string;
  /** Anropas när kopian landat, eller direkt om den inte flyger. */
  onLandad?: () => void;
};

type Aktiv = Flygning & { id: number };

const FlygContext = createContext<{ starta: (flygning: Flygning) => void } | null>(null);

/** Hur länge vi väntar på att målet ska dyka upp på den nya sidan innan vi ger upp och bara markerar. */
const MÅL_TIMEOUT_MS = 4000;
const LANDNINGSBREDD = 150;

// ----------------------------------------------------------------------

function ärReduceradRörelse(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

type KopiaProps = { flygning: Aktiv; onKlar: (id: number) => void };

/**
 * Kopian som flyger. Börjar där källan stod, väntar på att målet ska finnas (användaren navigerar
 * under tiden), och flyger sedan längs en båge och krymper ner i målet.
 *
 * Allt bygger på skärmkoordinater i en fast portal i `document.body`, så att kopian överlever att
 * sidan byts under den.
 */
function FlygandeKopia({ flygning, onKlar }: KopiaProps) {
  const { från } = flygning;
  const [mål, setMål] = useState<{ x: number; y: number } | null>(null);
  const klar = useRef(false);

  const avsluta = useCallback(() => {
    if (klar.current) return;
    klar.current = true;
    flygning.onLandad?.();
    onKlar(flygning.id);
  }, [flygning, onKlar]);

  const [spring, api] = useSpring(() => ({ t: 0, scale: 1, opacity: 0, lyft: 0 }));

  // Lyft upp kopian där den står, så att den känns som att den plockas loss.
  useEffect(() => {
    api.start({
      opacity: 1,
      lyft: 1,
      config: { tension: 280, friction: 20 },
    });
  }, [api]);

  // Leta upp målet: sidan kan ha ett annat innehåll en stund innan navigeringen är klar.
  useEffect(() => {
    const start = performance.now();
    let rAF = 0;
    const leta = () => {
      const el = document.querySelector(flygning.målVäljare);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          setMål({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
          return;
        }
      }
      if (performance.now() - start > MÅL_TIMEOUT_MS) {
        avsluta();
        return;
      }
      rAF = requestAnimationFrame(leta);
    };
    rAF = requestAnimationFrame(leta);
    return () => cancelAnimationFrame(rAF);
  }, [flygning.målVäljare, avsluta]);

  useEffect(() => {
    if (!mål) return;
    const slutskala = Math.min(1, LANDNINGSBREDD / Math.max(från.bredd, 1));
    const avstånd = Math.hypot(mål.x - (från.x + från.bredd / 2), mål.y - (från.y + från.höjd / 2));
    const duration = Math.min(1500, Math.max(800, 800 + avstånd * 0.5));
    api.start({
      to: async (next) => {
        await next({ t: 1, scale: slutskala, config: { duration, easing: easings.easeInOutCubic } });
        await next({ scale: slutskala * 0.55, opacity: 0, config: { duration: 280, easing: easings.easeInCubic } });
        avsluta();
      },
    });
  }, [mål, api, från, avsluta]);

  const startX = från.x + från.bredd / 2;
  const startY = från.y + från.höjd / 2;
  const dx = (mål?.x ?? startX) - startX;
  const dy = (mål?.y ?? startY) - startY;
  const båge = Math.min(200, Math.max(70, Math.hypot(dx, dy) * 0.3));

  const x = spring.t.to((t) => startX + dx * t);
  const y = spring.t.to((t) => startY + dy * t - båge * 4 * t * (1 - t));
  const rotate = spring.t.to((t) => Math.sin(t * Math.PI) * 9);
  const skugga = spring.lyft.to((l) => `0 ${6 + 24 * l}px ${14 + 36 * l}px rgba(0,0,0,${0.12 + 0.2 * l})`);
  const skalaMedLyft = spring.scale.to((s) => s);

  return (
    <animated.div
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        x,
        y,
        scale: skalaMedLyft,
        rotate,
        opacity: spring.opacity,
        translate: '-50% -50%',
        width: från.bredd,
        height: från.höjd,
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: skugga,
        background: 'rgba(255,255,255,0.92)',
        border: '1px solid rgba(255,255,255,0.6)',
        pointerEvents: 'none',
        zIndex: 2000,
        willChange: 'transform, opacity',
      }}
      aria-hidden
    >
      {flygning.bild ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={flygning.bild} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'grid',
            placeItems: 'center',
            font: '600 15px sans-serif',
            color: '#1c252e',
            padding: 12,
            textAlign: 'center',
          }}
        >
          {flygning.etikett}
        </div>
      )}
    </animated.div>
  );
}

// ----------------------------------------------------------------------

/**
 * Håller de flygningar som pågår. Ligger i dashboardens layout, som överlever sidbyten, så en kopia
 * kan lyfta från en sida och landa på en annan.
 */
export function FlygProvider({ children }: { children: React.ReactNode }) {
  const [flygningar, setFlygningar] = useState<Aktiv[]>([]);
  const nästa = useRef(1);
  // Portalen finns bara i webbläsaren. `false` på servern och vid första hydreringen, sedan `true`.
  const monterad = useSyncExternalStore(
    () => () => undefined,
    () => true,
    () => false
  );

  const starta = useCallback((flygning: Flygning) => {
    if (ärReduceradRörelse()) {
      // Ingen rörelse för den som bett om det; själva inläggningen är redan gjord.
      flygning.onLandad?.();
      return;
    }
    const id = nästa.current;
    nästa.current += 1;
    setFlygningar((nu) => [...nu, { ...flygning, id }]);
  }, []);

  const klar = useCallback((id: number) => setFlygningar((nu) => nu.filter((f) => f.id !== id)), []);

  const värde = useMemo(() => ({ starta }), [starta]);

  return (
    <FlygContext.Provider value={värde}>
      {children}
      {monterad &&
        createPortal(
          <>
            {flygningar.map((f) => (
              <FlygandeKopia key={f.id} flygning={f} onKlar={klar} />
            ))}
          </>,
          document.body
        )}
    </FlygContext.Provider>
  );
}

/** Startar en flygning. Utanför en provider händer ingenting, så att komponenter går att rendera i isolering. */
export function useFlygning() {
  const ctx = useContext(FlygContext);
  return useCallback((flygning: Flygning) => ctx?.starta(flygning), [ctx]);
}
